import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { Box, Typography, Button, Grid, Card, CardContent, Chip, Fade, Grow } from '@mui/material';
import { 
  AutoAwesome, 
  Description, 
  Rule, 
  TrendingUp, 
  PersonAdd, 
  CheckCircle,
  PlayArrow,
  CalendarToday,
  Bolt,
  Security,
  Timer,
  Verified,
  Dashboard,
  CloudUpload,
  BarChart,
  FileCopy
} from '@mui/icons-material';
import { CenteredContainer } from '../../components/common';
import { Footer } from '../../components/layout';
import '../../styles/App.css';

function RSNA2025({ isMobile, theme }) {
  const [visibleSections, setVisibleSections] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-section]');
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <div style={{
        fontFamily: 'Open Sans, Arial, sans-serif',
        lineHeight: '1.6',
        margin: '0 auto',
        overflowX: 'hidden',
      }}>
        
        {/* HERO SECTION */}
        <section 
          id="hero"
          data-section="hero"
          style={{
            background: 'linear-gradient(135deg, #0A1929 0%, #0A3D62 50%, #0d4d7a 100%)',
            padding: isMobile ? '80px 20px 60px' : '120px 20px 100px',
            position: 'relative',
            overflow: 'hidden',
            minHeight: isMobile ? 'auto' : '90vh',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* Background Elements */}
          <Box sx={{
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: '600px',
            height: '600px',
            background: 'radial-gradient(circle, rgba(255, 107, 53, 0.15) 0%, transparent 70%)',
            borderRadius: '50%',
          }} />
          
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '5px',
            background: 'linear-gradient(90deg, #FF6B35 0%, #17A2B8 50%, #FF6B35 100%)',
            backgroundSize: '200% 100%',
          }} />
          
          <CenteredContainer>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={7}>
                <Fade in timeout={800}>
                  <Box>
                    <Chip
                      icon={<Bolt sx={{ color: '#FF6B35 !important' }} />}
                      label="RSNA 2025"
                      sx={{
                        backgroundColor: 'rgba(255, 107, 53, 0.15)',
                        border: '1px solid rgba(255, 107, 53, 0.4)',
                        color: '#FF6B35',
                        fontWeight: '700',
                        marginBottom: '30px',
                      }}
                    />
                    
                    <Typography variant="h1" sx={{ 
                      color: '#FFFFFF', 
                      fontWeight: '800',
                      fontSize: isMobile ? '2.5rem' : '4rem',
                      marginBottom: '24px',
                    }}>
                      From DXA Scan to Complete BMD Report in{' '}
                      <Box component="span" sx={{ color: '#FF6B35' }}>30 Seconds</Box>
                    </Typography>
                    
                    <Typography variant="h5" sx={{ color: '#E0E0E0', marginBottom: '40px' }}>
                      See up to 10 more patients per day by reducing technologist documentation and automating BMD reporting.
                    </Typography>

                    <Box sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '12px',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      padding: '12px 20px',
                      borderRadius: '12px',
                      marginBottom: '40px',
                    }}>
                      <Verified sx={{ color: '#17A2B8' }} />
                      <Typography sx={{ color: '#FFFFFF', fontSize: '0.875rem', fontWeight: '600' }}>
                        Fully compliant with CAR 2025 & Osteoporosis Canada 2024 guidelines
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '16px', marginTop: '40px' }}>
                      <Button 
                        variant="contained"
                        size="large"
                        href="https://calendly.com/c-encode/demo"
                        target="_blank"
                        startIcon={<CalendarToday />}
                        sx={{
                          background: 'linear-gradient(135deg, #FF6B35 0%, #E55A2B 100%)',
                          padding: '16px 40px',
                          fontSize: '1.125rem',
                          fontWeight: '700',
                          borderRadius: '12px',
                          textTransform: 'none',
                        }}
                      >
                        Book a Demo
                      </Button>
                    </Box>
                  </Box>
                </Fade>
              </Grid>

              <Grid item xs={12} md={5}>
                <Grow in timeout={1000}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '40px' }}>
                    {[
                      { icon: <Timer />, color: '#FF6B35', title: '40% less tech time per case', subtitle: '25 min → 15 min' },
                      { icon: <PersonAdd />, color: '#17A2B8', title: '56% more cases scanned', subtitle: '18 → 28 patients/day' },
                      { icon: <FileCopy />, color: '#9C27B0', title: '90% faster reporting', subtitle: '5 min → less than 30s' },
                    ].map((stat, index) => (
                      <Card key={index} sx={{
                        background: `linear-gradient(135deg, ${stat.color}40 0%, ${stat.color}20 100%)`,
                        border: `1px solid ${stat.color}60`,
                        borderRadius: '16px',
                        padding: '20px 18px',
                      }}>
                        <Box>
                          {React.cloneElement(stat.icon, { sx: { fontSize: '28px', color: stat.color, marginBottom: '12px' } })}
                          <Typography sx={{ color: '#FFFFFF', fontSize: '1.25rem', fontWeight: '800' }}>
                            {stat.title}
                          </Typography>
                        </Box>
                        <Typography sx={{ color: '#FFFFFF', fontSize: '0.875rem', opacity: 0.95 }}>
                          {stat.subtitle}
                        </Typography>
                      </Card>
                    ))}
                  </Box>
                </Grow>
              </Grid>
            </Grid>
          </CenteredContainer>
        </section>

        {/* VIDEO SECTION */}
        <Box id="demo-video" data-section="demo-video" sx={{
          background: 'linear-gradient(135deg, #0A1929 0%, #0A3D62 100%)',
          padding: isMobile ? '80px 20px' : '120px 20px',
        }}>
          <CenteredContainer>
            <Box sx={{ textAlign: 'center', marginBottom: '60px' }}>
              <PlayArrow sx={{ fontSize: '56px', color: '#FF6B35', marginBottom: '24px' }} />
              <Typography variant="h2" sx={{ color: '#FFFFFF', fontSize: isMobile ? '2rem' : '3rem', fontWeight: '800' }}>
                Introducing: DXA Encode
              </Typography>
            </Box>

            <Box sx={{
              maxWidth: isMobile ? '100%' : '1000px',
              margin: '0 auto',
              borderRadius: '24px',
              overflow: 'hidden',
              boxShadow: '0 24px 80px rgba(0, 0, 0, 0.5)',
            }}>
              <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                <Box
                  component="iframe"
                  src="https://www.loom.com/embed/fc900a52f3db45e89f71f3eb0c14e8f1"
                  frameBorder="0"
                  allowFullScreen
                  sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                />
              </Box>
            </Box>
          </CenteredContainer>
        </Box>

        {/* WORKFLOW SECTION */}
        <Box id="workflow" data-section="workflow" sx={{
          background: 'linear-gradient(to bottom, #FFFFFF 0%, #F8F9FA 100%)',
          padding: isMobile ? '80px 20px' : '120px 20px',
        }}>
          <CenteredContainer>
            <Box sx={{ textAlign: 'center', marginBottom: '60px' }}>
              <Chip icon={<AutoAwesome />} label="Complete Automation" sx={{ backgroundColor: 'rgba(255, 107, 53, 0.1)', color: '#FF6B35', fontWeight: '700', marginBottom: '24px' }} />
              <Typography variant="h2" sx={{ color: '#0A3D62', fontSize: isMobile ? '2rem' : '3rem', fontWeight: '800' }}>
                Complete Automation, Zero Maintenance
              </Typography>
            </Box>
            
            <Grid container spacing={4}>
              {[
                { icon: <CloudUpload sx={{ fontSize: '48px' }} />, title: 'Automatic Data Capture', description: 'DXA Encode automatically pulls measurements and patient history details directly from the DXA scanner.', gradient: 'linear-gradient(135deg, #0A3D62 0%, #0d4d7a 100%)' },
                { icon: <Description sx={{ fontSize: '48px' }} />, title: 'Complete BMD Reports', description: 'Using templates customized to your practice\'s needs, radiologist reports are pre-populated and ready for sign-off.', gradient: 'linear-gradient(135deg, #17A2B8 0%, #4BAFD1 100%)' },
                { icon: <Rule sx={{ fontSize: '48px' }} />, title: 'Turn-key Guideline Compliance', description: 'Latest regional guidelines are built in, with any updates handled and ready for your approval.', gradient: 'linear-gradient(135deg, #FF6B35 0%, #E55A2B 100%)' },
              ].map((item, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Grow in={visibleSections.workflow} timeout={600 + index * 200}>
                    <Card sx={{ height: '100%', borderRadius: '20px', boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, width: '100%', height: '5px', background: item.gradient } }}>
                      <CardContent sx={{ padding: '40px 32px' }}>
                        <Box sx={{ width: '80px', height: '80px', borderRadius: '16px', background: item.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px', color: '#FFFFFF' }}>
                          {item.icon}
                        </Box>
                        <Typography variant="h5" sx={{ color: '#0A3D62', marginBottom: '16px', fontWeight: '700' }}>{item.title}</Typography>
                        <Typography variant="body1" sx={{ color: '#2E3A45' }}>{item.description}</Typography>
                      </CardContent>
                    </Card>
                  </Grow>
                </Grid>
              ))}
            </Grid>
          </CenteredContainer>
        </Box>

        {/* WHY IT MATTERS */}
        <Box id="why-matters" data-section="why-matters" sx={{
          background: 'linear-gradient(135deg, #0A1929 0%, #0A3D62 100%)',
          padding: isMobile ? '80px 20px' : '120px 20px',
        }}>
          <CenteredContainer>
            <Box sx={{ textAlign: 'center', marginBottom: '60px' }}>
              <TrendingUp sx={{ fontSize: '56px', color: '#FF6B35', marginBottom: '24px' }} />
              <Typography variant="h2" sx={{ color: '#FFFFFF', fontSize: isMobile ? '2rem' : '3rem', fontWeight: '800' }}>
                Why Canadian Sites Need This Now
              </Typography>
            </Box>
            
            <Grid container spacing={4}>
              {[
                { icon: <Rule />, title: 'Updated CAR 2025 Guidelines', description: 'Increased complexity with more diagnostic criteria, risk calculations, and compliance requirements', color: '#FF6B35' },
                { icon: <PersonAdd />, title: 'Technologist Time Constraints', description: 'Manual data entry and documentation is limiting how many patients can be scanned per day', color: '#17A2B8' },
                { icon: <BarChart />, title: 'Growing Patient Volumes', description: 'Increasing demand for DXA scans requires higher throughput without additional staff', color: '#FF6B35' },
              ].map((item, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Fade in={visibleSections['why-matters']} timeout={600 + index * 200}>
                    <Card sx={{ height: '100%', background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '20px', padding: '40px 32px' }}>
                      <Box sx={{ width: '72px', height: '72px', borderRadius: '16px', backgroundColor: `${item.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px', color: item.color }}>
                        {React.cloneElement(item.icon, { sx: { fontSize: '40px' } })}
                      </Box>
                      <Typography variant="h5" sx={{ color: '#FFFFFF', marginBottom: '16px', fontWeight: '700' }}>{item.title}</Typography>
                      <Typography variant="body1" sx={{ color: '#E0E0E0' }}>{item.description}</Typography>
                    </Card>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </CenteredContainer>
        </Box>

        {/* CASE STUDY */}
        <Box id="case-study" data-section="case-study" sx={{ background: '#FFFFFF', padding: isMobile ? '80px 20px' : '120px 20px' }}>
          <CenteredContainer>
            <Fade in={visibleSections['case-study']} timeout={800}>
              <Card sx={{ borderRadius: '24px', padding: isMobile ? '50px 30px' : '70px 60px', boxShadow: '0 12px 48px rgba(0, 0, 0, 0.1)', position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: 0, left: 0, width: '6px', height: '100%', background: 'linear-gradient(180deg, #FF6B35 0%, #17A2B8 100%)' }} />
                <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '30px' }}>
                  <Box sx={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #17A2B8 0%, #4BAFD1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckCircle sx={{ fontSize: '48px', color: '#FFFFFF' }} />
                  </Box>
                </Box>
                <Typography variant="h3" sx={{ color: '#0A3D62', textAlign: 'center', marginBottom: '30px', fontWeight: '800' }}>Proven Results</Typography>
                <Typography variant="h6" sx={{ textAlign: 'center', color: '#2E3A45', maxWidth: '900px', margin: '0 auto' }}>
                  At a major Ontarian hospital network, DXA Encode reduced technologist time per case from{' '}
                  <Box component="span" sx={{ fontWeight: '700', color: '#0A3D62' }}>25 minutes</Box> to{' '}
                  <Box component="span" sx={{ fontWeight: '700', color: '#FF6B35' }}>15 minutes</Box>,
                  enabling the clinic to increase capacity from 18 to 28 patients per day.
                </Typography>
              </Card>
            </Fade>
          </CenteredContainer>
        </Box>

        {/* CTA SECTION */}
        <Box id="cta" data-section="cta" sx={{ background: 'linear-gradient(135deg, #1D1D1D 0%, #2E3A45 100%)', padding: isMobile ? '80px 20px' : '120px 20px' }}>
          <CenteredContainer>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ backgroundColor: 'rgba(255, 107, 53, 0.2)', borderRadius: '50%', width: '100px', height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 30px' }}>
                <PlayArrow sx={{ fontSize: '50px', color: '#FF6B35' }} />
              </Box>
              <Typography variant="h2" sx={{ color: '#FFFFFF', fontSize: isMobile ? '2rem' : '3rem', fontWeight: '800', marginBottom: '20px' }}>
                Ready to Transform Your DXA Workflow?
              </Typography>
              <Typography variant="h6" sx={{ color: '#E0E0E0', marginBottom: '50px', maxWidth: '600px', margin: '0 auto 50px' }}>
                See how DXA Encode can increase your scan capacity without adding headcount
              </Typography>
              <Button 
                variant="contained"
                size="large"
                href="https://calendly.com/c-encode/demo"
                target="_blank"
                startIcon={<CalendarToday />}
                sx={{
                  background: 'linear-gradient(135deg, #FF6B35 0%, #E55A2B 100%)',
                  padding: '20px 40px',
                  fontSize: '1.125rem',
                  fontWeight: '700',
                  borderRadius: '12px',
                  textTransform: 'none',
                }}
              >
                Book a Demo
              </Button>
            </Box>
          </CenteredContainer>
        </Box>

        {/* TRUST BADGES */}
        <Box sx={{ background: '#FFFFFF', padding: isMobile ? '50px 20px' : '70px 20px', borderTop: '1px solid #E0E0E0' }}>
          <CenteredContainer>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: '30px', padding: '40px', backgroundColor: '#F8F9FA', borderRadius: '16px' }}>
              {[
                { icon: <Security />, text: 'Built in Canada' },
                { icon: <Verified />, text: 'PHIPA compliant' },
                { icon: <Dashboard />, text: 'Deployed across Canadian hospitals & clinics' },
              ].map((item, index) => (
                <React.Fragment key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Box sx={{ width: '48px', height: '48px', borderRadius: '12px', background: 'linear-gradient(135deg, #17A2B8 0%, #4BAFD1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF' }}>
                      {item.icon}
                    </Box>
                    <Typography variant="body1" sx={{ color: '#2E3A45', fontWeight: '600' }}>{item.text}</Typography>
                  </Box>
                  {index < 2 && <Box sx={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#B0BEC5' }} />}
                </React.Fragment>
              ))}
            </Box>
          </CenteredContainer>
        </Box>

        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default RSNA2025;

